import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { useAuth } from "@/hooks/use-auth";
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, Area, AreaChart
} from "recharts";
import { 
  TrendingUp, DollarSign, Calendar, Users, Music, 
  CheckCircle, XCircle, Clock, BarChart3, PieChart as PieChartIcon,
  Building, Star, AlertCircle, ArrowUpRight, ArrowDownRight,
  CreditCard, FileSignature, Percent, Target, Activity,
  RefreshCw, Loader2, Download, Filter
} from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subDays } from "date-fns";

// Interfaces for analytics data
interface BookingAnalyticsOverview {
  // Financial Metrics - ACTUAL (completed bookings only)
  totalRevenue: number;
  totalCommission: number;
  netRevenue: number;
  averageBookingValue: number;
  completedPayments: number;
  outstandingPayments: number;
  
  // Financial Metrics - PROJECTED (confirmed bookings)
  projectedRevenue: number;
  projectedCommission: number;
  projectedNetRevenue: number;
  
  // Combined Financial Metrics
  combinedRevenue: number;
  combinedCommission: number;
  combinedNetRevenue: number;
  
  // Booking Metrics
  totalBookings: number;
  confirmedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  completedBookings: number;
  bookingConversionRate: number;
  cancellationRate: number;
  completionRate: number;
  
  // Act Metrics
  totalActs: number;
  activeActs: number;
  averageFeePerAct: number;
  actsWithMultipleBookings: number;
  
  // Payment Metrics
  paidBookings: number;
  unpaidBookings: number;
  paymentRate: number;
  
  // Time-based Comparisons
  revenueGrowth: number; // % vs previous period
  bookingGrowth: number; // % vs previous period
  commissionGrowth: number; // % vs previous period
  
  // Deposit Metrics
  depositCompletionRate: number;
}

interface RevenueTimeSeries {
  date: string;
  // Actual (completed only)
  actualRevenue: number;
  actualCommission: number;
  actualNetRevenue: number;
  completedBookings: number;
  // Projected (confirmed only)
  projectedRevenue: number;
  projectedCommission: number;
  projectedNetRevenue: number;
  confirmedBookings: number;
  // Combined totals for backwards compatibility
  revenue: number;
  commission: number;
  bookings: number;
  netRevenue: number;
}

interface ActPerformance {
  actId: number;
  actName: string;
  bookingCount: number;
  // Actual revenue (completed bookings only)
  totalRevenue: number;
  totalCommission: number;
  // Projected revenue (confirmed bookings)
  projectedRevenue?: number;
  projectedCommission?: number;
  averageFee: number;
  completedCount?: number;
  confirmedCount?: number;
  cancelledCount?: number;
  pendingCount?: number;
  paidCount?: number;
  cancellationRate?: number;
  confirmationRate?: number;
  paymentRate?: number;
  netRevenue?: number;
  lastBookingDate?: string;
  rating?: number;
  genres?: string[];
}

interface VenuePerformance {
  venueName: string;
  bookingCount: number;
  totalRevenue: number;
  averageBookingValue: number;
  topAct?: string;
}

interface CommissionBreakdown {
  type: 'percentage' | 'flat';
  count: number;
  totalAmount: number;
  averageAmount: number;
}

interface BookingStatusDistribution {
  status: string;
  count: number;
  percentage: number;
  revenue: number;
}

interface PaymentStatusDistribution {
  status: string;
  count: number;
  amount: number;
  percentage: number;
}

export default function BookingAnalytics() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { organisationId } = useParams<{ organisationId: string }>();
  const [, setLocation] = useLocation();
  
  // Time period selection
  const [timePeriod, setTimePeriod] = useState<string>("this-month");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  
  // Filters
  const [actFilter, setActFilter] = useState<string>("all");
  const [venueFilter, setVenueFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  const currentOrgId = organisationId ? parseInt(organisationId) : null;
  
  // Get current organization info
  const currentOrg = user?.organisationUsers?.find(ou => ou.organisation.id === currentOrgId)?.organisation;
  
  // Check if user has access to this organization
  const hasAccess = user?.role === 'god' || user?.organisationUsers?.some(ou => 
    ou.organisation.id === currentOrgId && (ou.role === 'owner' || ou.role === 'operator')
  );
  
  // Calculate date range based on time period
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (timePeriod) {
      case "today":
        startDate = format(now, 'yyyy-MM-dd');
        endDate = format(now, 'yyyy-MM-dd');
        break;
      case "this-week":
        startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        endDate = format(now, 'yyyy-MM-dd');
        break;
      case "this-month":
        startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        endDate = format(endOfMonth(now), 'yyyy-MM-dd');
        break;
      case "last-month":
        const lastMonth = subMonths(now, 1);
        startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case "this-year":
        startDate = format(startOfYear(now), 'yyyy-MM-dd');
        endDate = format(endOfYear(now), 'yyyy-MM-dd');
        break;
      case "all-time":
        startDate = "";
        endDate = "";
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        endDate = format(endOfMonth(now), 'yyyy-MM-dd');
    }
    
    return { startDate, endDate };
  };
  
  const { startDate, endDate } = getDateRange();
  
  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (currentOrgId) params.append('organisationId', currentOrgId.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (actFilter !== 'all') params.append('actId', actFilter);
    if (venueFilter !== 'all') params.append('venue', venueFilter);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    return params.toString();
  };
  
  // Fetch overview data
  const { data: overview, isLoading: overviewLoading, refetch: refetchOverview } = useQuery<BookingAnalyticsOverview>({
    queryKey: [`/api/analytics/bookings/overview?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
  
  // Fetch revenue time series
  const { data: revenueSeries, isLoading: revenueLoading } = useQuery<RevenueTimeSeries[]>({
    queryKey: [`/api/analytics/bookings/revenue-series?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch act performance data
  const { data: actPerformance, isLoading: actsLoading } = useQuery<ActPerformance[]>({
    queryKey: [`/api/analytics/bookings/act-performance?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch venue performance data
  const { data: venuePerformance } = useQuery<VenuePerformance[]>({
    queryKey: [`/api/analytics/bookings/venue-performance?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch commission breakdown
  const { data: commissionBreakdown } = useQuery<CommissionBreakdown[]>({
    queryKey: [`/api/analytics/bookings/commission-breakdown?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch booking status distribution
  const { data: bookingStatusDist } = useQuery<BookingStatusDistribution[]>({
    queryKey: [`/api/analytics/bookings/status-distribution?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch payment status distribution
  const { data: paymentStatusDist } = useQuery<PaymentStatusDistribution[]>({
    queryKey: [`/api/analytics/bookings/payment-distribution?${buildQueryParams()}`],
    enabled: !!currentOrgId && hasAccess,
    staleTime: 2 * 60 * 1000,
  });
  
  // Fetch available acts for filter
  const { data: availableActs } = useQuery({
    queryKey: [`/api/acts?organisationId=${currentOrgId}`],
    enabled: !!currentOrgId && hasAccess,
  });
  
  // Fetch available venues for filter
  const { data: availableVenues } = useQuery({
    queryKey: [`/api/analytics/bookings/venues?organisationId=${currentOrgId}`],
    enabled: !!currentOrgId && hasAccess,
  });
  
  // Format currency
  const formatCurrency = (amount: number) => {
    // Convert from cents to pounds and handle null/undefined
    if (!amount || isNaN(amount)) return '£0.00';
    const pounds = amount / 100;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(pounds);
  };
  
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  
  // Get trend icon and color
  const getTrendIndicator = (value: number) => {
    if (value > 0) {
      return <ArrowUpRight className="h-4 w-4 text-green-600" />;
    } else if (value < 0) {
      return <ArrowDownRight className="h-4 w-4 text-red-600" />;
    }
    return null;
  };
  
  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  
  // Status-specific colors for consistency
  const STATUS_COLORS: Record<string, string> = {
    'completed': '#10b981', // green
    'confirmed': '#3b82f6', // blue
    'cancelled': '#ef4444', // red
    'pending': '#f59e0b',   // amber
    'draft': '#6b7280',     // gray
  };
  
  // Payment status colors
  const PAYMENT_STATUS_COLORS: Record<string, string> = {
    'paid': '#10b981',      // green
    'unpaid': '#ef4444',    // red
    'partial': '#f59e0b',   // amber
    'pending': '#3b82f6',   // blue
    'overdue': '#dc2626',   // dark red
  };
  
  // Helper to get color for booking status
  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status.toLowerCase()] || '#8b5cf6'; // purple as fallback
  };
  
  // Helper to get color for payment status
  const getPaymentStatusColor = (status: string) => {
    return PAYMENT_STATUS_COLORS[status.toLowerCase()] || '#8b5cf6'; // purple as fallback
  };
  
  if (isAuthLoading) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </PageBackground>
    );
  }
  
  if (!hasAccess || !currentOrgId) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-4">You don't have permission to view booking analytics.</p>
            <Button onClick={() => setLocation('/')} variant="outline">
              Go Back
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }
  
  return (
    <PageBackground>
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Booking Analytics</h1>
              <p className="text-slate-600">{currentOrg?.name}</p>
            </div>
            
            {/* Time Period and Filters */}
            <div className="flex flex-wrap gap-2">
              <Select value={timePeriod} onValueChange={setTimePeriod}>
                <SelectTrigger className="w-[180px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="this-week">This Week</SelectItem>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="this-year">This Year</SelectItem>
                  <SelectItem value="all-time">All Time</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchOverview()}
                disabled={overviewLoading}
              >
                <RefreshCw className={`h-4 w-4 ${overviewLoading ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
          {/* Actual Revenue Card */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Actual Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(overview?.totalRevenue || 0)}
              </div>
              <div className="text-xs text-slate-600">
                From {overview?.completedBookings || 0} completed bookings
              </div>
              {overview?.projectedRevenue > 0 && (
                <div className="text-xs text-blue-600 mt-1">
                  +{formatCurrency(overview?.projectedRevenue || 0)} projected
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Commission Card */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Commission Earned</CardTitle>
              <Percent className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(overview?.totalCommission || 0)}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span className={overview?.commissionGrowth >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {getTrendIndicator(overview?.commissionGrowth || 0)}
                </span>
                <span>{formatPercentage(Math.abs(overview?.commissionGrowth || 0))} vs last period</span>
              </div>
            </CardContent>
          </Card>
          
          {/* Bookings Card */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
              <Calendar className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.totalBookings || 0}</div>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="secondary" className="text-xs">
                  {overview?.confirmedBookings || 0} confirmed
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {overview?.pendingBookings || 0} pending
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          {/* Average Booking Value */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Booking Value</CardTitle>
              <Target className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(overview?.averageBookingValue || 0)}
              </div>
              <div className="text-xs text-slate-600">
                Per confirmed booking
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs for different views */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="acts">Acts</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Revenue Chart */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Revenue & Commission Trends</CardTitle>
                <CardDescription>Track your revenue and commission over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={revenueSeries}>
                    <defs>
                      <linearGradient id="colorActualRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorProjectedRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="actualRevenue" 
                      stroke="#10b981" 
                      fillOpacity={1} 
                      fill="url(#colorActualRevenue)"
                      name="Actual Revenue"
                      stackId="1"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="projectedRevenue" 
                      stroke="#3b82f6" 
                      fillOpacity={1} 
                      fill="url(#colorProjectedRevenue)"
                      name="Projected Revenue"
                      stackId="1"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="actualCommission" 
                      stroke="#8b5cf6" 
                      fillOpacity={1} 
                      fill="url(#colorCommission)"
                      name="Commission"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <div className="grid gap-4 md:grid-cols-2">
              {/* Booking Status Distribution */}
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle>Booking Status</CardTitle>
                  <CardDescription>Distribution of booking statuses</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={bookingStatusDist}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.status}: ${entry.count}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {bookingStatusDist?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              
              {/* Payment Status */}
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle>Payment Status</CardTitle>
                  <CardDescription>Track payment collection</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Completed Payments</span>
                      <span className="text-2xl font-bold text-green-600">
                        {formatCurrency(overview?.completedPayments || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Outstanding</span>
                      <span className="text-2xl font-bold text-amber-600">
                        {formatCurrency(overview?.outstandingPayments || 0)}
                      </span>
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Deposit Completion</span>
                        <span className="text-lg font-semibold">
                          {formatPercentage(overview?.depositCompletionRate || 0)}
                        </span>
                      </div>
                      <div className="mt-2 bg-white/20 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${overview?.depositCompletionRate || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              {/* Net Revenue Card */}
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle>Net Revenue</CardTitle>
                  <CardDescription>After commission</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {formatCurrency(overview?.netRevenue || 0)}
                  </div>
                </CardContent>
              </Card>
              
              {/* Outstanding Payments */}
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle>Outstanding</CardTitle>
                  <CardDescription>Awaiting payment</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-amber-600">
                    {formatCurrency(overview?.outstandingPayments || 0)}
                  </div>
                </CardContent>
              </Card>
              
              {/* Deposits */}
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle>Deposits Collected</CardTitle>
                  <CardDescription>{overview?.totalDepositsPaid || 0} of {overview?.totalDepositsRequired || 0}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {formatPercentage(overview?.depositCompletionRate || 0)}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Commission Breakdown */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Commission Analysis</CardTitle>
                <CardDescription>Breakdown by commission type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={commissionBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Bar dataKey="totalAmount" fill="#8b5cf6" name="Total Commission" />
                    <Bar dataKey="averageAmount" fill="#3b82f6" name="Average Commission" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Revenue by Payment Status */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Revenue by Payment Status</CardTitle>
                <CardDescription>Track payment collection progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {paymentStatusDist?.map((status) => (
                    <div key={status.status} className="flex items-center justify-between p-3 rounded-lg bg-white/10">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getPaymentStatusColor(status.status) }}
                        />
                        <span className="font-medium capitalize">{status.status}</span>
                        <Badge variant="secondary">{status.count} bookings</Badge>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(status.amount)}</p>
                        <p className="text-xs text-slate-600">{formatPercentage(status.percentage)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Acts Tab */}
          <TabsContent value="acts" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3 mb-4">
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Total Acts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.totalActs || 0}</div>
                  <p className="text-xs text-slate-600">{overview?.activeActs || 0} with bookings</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Avg Fee per Act</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(overview?.averageFeePerAct || 0)}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Repeat Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.actsWithMultipleBookings || 0}</div>
                  <p className="text-xs text-slate-600">Acts with 2+ bookings</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Top Performing Acts */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Top Performing Acts</CardTitle>
                <CardDescription>Ranked by total revenue generated</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {actPerformance?.slice(0, 10).map((act, index) => (
                    <div key={act.actId} className="flex items-center justify-between p-4 rounded-lg bg-white/10 hover:bg-white/20 transition-all">
                      <div className="flex items-center gap-4">
                        <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center">
                          {index + 1}
                        </Badge>
                        <div>
                          <p className="font-medium">{act.actName}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {act.genres?.map((genre, idx) => (
                              <Badge key={`${act.actId}-genre-${idx}`} variant="outline" className="text-xs">
                                {genre}
                              </Badge>
                            ))}
                            {act.completedCount > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {act.completedCount} completed
                              </Badge>
                            )}
                            {act.cancellationRate !== undefined && act.cancellationRate > 0 && (
                              <Badge 
                                variant={act.cancellationRate > 20 ? "destructive" : "outline"} 
                                className="text-xs"
                              >
                                {act.cancellationRate.toFixed(1)}% cancelled
                              </Badge>
                            )}
                            {act.paymentRate !== undefined && act.paymentRate < 100 && (
                              <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                                {act.paymentRate.toFixed(0)}% paid
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(act.totalRevenue || 0)}</p>
                        {act.projectedRevenue > 0 && (
                          <p className="text-sm text-blue-600">+{formatCurrency(act.projectedRevenue || 0)} projected</p>
                        )}
                        <div className="text-sm text-slate-600">
                          {act.bookingCount || 0} total bookings
                        </div>
                        <p className="text-xs text-slate-500">
                          Avg: {formatCurrency(act.averageFee || 0)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Act Performance Chart */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Revenue by Act</CardTitle>
                <CardDescription>Top 10 acts by revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={actPerformance?.slice(0, 10)} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="actName" type="category" width={100} />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Bar dataKey="totalRevenue" fill="#3b82f6" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Act Performance Statistics */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Act Performance Metrics</CardTitle>
                <CardDescription>Detailed statistics for each act</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-2">Act</th>
                        <th className="text-center py-2">Total</th>
                        <th className="text-center py-2">Confirmed</th>
                        <th className="text-center py-2">Cancelled</th>
                        <th className="text-center py-2">Cancel Rate</th>
                        <th className="text-center py-2">Payment Rate</th>
                        <th className="text-right py-2">Net Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actPerformance?.slice(0, 10).map((act) => (
                        <tr key={act.actId} className="border-b border-white/10">
                          <td className="py-2 font-medium">{act.actName}</td>
                          <td className="text-center py-2">{act.bookingCount || 0}</td>
                          <td className="text-center py-2">{act.confirmedCount || 0}</td>
                          <td className="text-center py-2">{act.cancelledCount || 0}</td>
                          <td className="text-center py-2">
                            <Badge 
                              variant={act.cancellationRate && act.cancellationRate > 20 ? "destructive" : "secondary"}
                              className="text-xs"
                            >
                              {act.cancellationRate?.toFixed(1) || 0}%
                            </Badge>
                          </td>
                          <td className="text-center py-2">
                            <Badge 
                              variant={act.paymentRate === 100 ? "default" : "outline"}
                              className="text-xs"
                            >
                              {act.paymentRate?.toFixed(0) || 0}%
                            </Badge>
                          </td>
                          <td className="text-right py-2 font-semibold">
                            {formatCurrency(act.netRevenue || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3 mb-4">
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Conversion Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatPercentage(overview?.bookingConversionRate || 0)}</div>
                  <p className="text-xs text-slate-600">Confirmed / Total</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Cancellation Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {formatPercentage(overview?.cancellationRate || 0)}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/20 backdrop-blur-xl border-white/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Completed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.completedBookings || 0}</div>
                  <p className="text-xs text-slate-600">Successfully delivered</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Booking Trends */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Booking Volume Trends</CardTitle>
                <CardDescription>Number of bookings over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="bookings" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Bookings"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Venue Performance */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Top Venues</CardTitle>
                <CardDescription>By booking count and revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {venuePerformance?.slice(0, 10).map((venue, index) => (
                    <div key={venue.venueName} className="flex items-center justify-between p-3 rounded-lg bg-white/10">
                      <div className="flex items-center gap-3">
                        <Building className="h-4 w-4 text-slate-600" />
                        <div>
                          <p className="font-medium">{venue.venueName}</p>
                          {venue.topAct && (
                            <p className="text-xs text-slate-600">Top act: {venue.topAct}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(venue.totalRevenue)}</p>
                        <p className="text-xs text-slate-600">
                          {venue.bookingCount} bookings • Avg {formatCurrency(venue.averageBookingValue)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Booking Status Breakdown with Revenue */}
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <CardTitle>Revenue by Booking Status</CardTitle>
                <CardDescription>Financial impact of each status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={bookingStatusDist}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </PageBackground>
  );
}